"use client"

import * as React from "react"
import { Link } from "react-router"
import { KeyMetrics } from "@/components/key-metrics"
import { LocalBanner } from "@/components/ui/banner"
import { Card, CardContent, CardFooter } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { StatusBadge } from "@/components/ui/status-badge"
import {
  EvaluationTermCardHeader,
  EvaluationTermViewDetailsButton,
} from "@/components/evaluation-dashboard/evaluation-term-card-header"
import { evaluationAnalyticsPath } from "@/lib/evaluation-routes"
import { evaluationFocusTermCardMetrics } from "@/lib/mock/evaluation-dashboard-kpi"
import type { EvaluationTerm } from "@/lib/mock/evaluation-dashboard"
import { useProduct } from "@/contexts/product-context"
import { cn } from "@/lib/utils"

/**
 * Flat KeyMetrics ships hub gutters (`px-4 lg:px-6`). Inside a term card, mount
 * the strip on the Card shell (not CardContent) and swap to `--card-spacing`.
 */
const FOCUS_TERM_KPI_CLASS = cn(
  "w-full max-w-none shrink-0 border-t border-border/60",
  "pt-2 pb-2 sm:pt-2.5 sm:pb-3 lg:pt-3 lg:pb-4",
  "[&_[data-slot=key-metrics]>div]:px-(--card-spacing)",
  "[&_[data-slot=key-metrics]>div]:lg:px-(--card-spacing)",
)

interface EvaluationAnalyticFocusTermProps {
  term: EvaluationTerm
  /** Header + KPI strip only (paired operations layout). */
  compact?: boolean
  /** Half-width column beside live term — compact lifecycle + condensed queue. */
  paired?: boolean
  className?: string
}

export function EvaluationAnalyticFocusTerm({
  term,
  compact = false,
  paired = false,
  className,
}: EvaluationAnalyticFocusTermProps) {
  const { product } = useProduct()
  const metrics = React.useMemo(() => evaluationFocusTermCardMetrics(term), [term])
  const attention = term.attention
  const showAttention = attention != null && !compact
  const showBody = paired || showAttention

  return (
    <Card
      size="sm"
      id={`term-${term.id}`}
      className={cn("flex h-auto min-h-min flex-col overflow-visible", className)}
    >
      <EvaluationTermCardHeader
        term={term}
        trailing={
          term.needsAttention ? (
            <StatusBadge
              label="Needs attention"
              tone="warning"
              icon="fa-triangle-exclamation"
              size="md"
            />
          ) : null
        }
      />

      {metrics.length > 0 ? (
        <KeyMetrics
          variant="flat"
          size="md"
          metrics={metrics}
          showHeader={false}
          metricsSingleRow
          className={FOCUS_TERM_KPI_CLASS}
        />
      ) : null}

      {showBody ? (
        <CardContent className={cn("flex flex-col gap-4", paired && "flex-1")}>
          {showAttention ? (
            <LocalBanner variant="warning">
              {attention.coursesBelowThreshold} courses and{" "}
              {attention.facultyRolesBelowThreshold} faculty roles scored below threshold.
            </LocalBanner>
          ) : null}
        </CardContent>
      ) : null}

      {paired ? (
        <CardFooter className="mt-auto flex flex-wrap gap-2 border-t border-border/60 pt-4">
          <Button size="sm" asChild>
            <Link to={evaluationAnalyticsPath(product, term.id)}>View analytics</Link>
          </Button>
          <Button size="sm" variant="outline">
            Export summary
          </Button>
          <EvaluationTermViewDetailsButton term={term} />
        </CardFooter>
      ) : !compact ? (
        <CardFooter className="mt-auto flex flex-wrap gap-2 border-t border-border/60 pt-4">
          <Button size="sm" asChild>
            <Link to={evaluationAnalyticsPath(product, term.id)}>View analytics</Link>
          </Button>
          <Button size="sm" variant="outline">
            Export summary
          </Button>
          <EvaluationTermViewDetailsButton term={term} />
        </CardFooter>
      ) : null}
    </Card>
  )
}
